import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';
import VinCheckerForm from '../vinCheckerForm/VinCheckerForm';
import styles from './VinChecker.module.scss';
import {
	AppLinksV2Labels,
	VinCheckerMainLabels
} from '../../@types/labelTypes';
import {
	featuresAndCapabilitiesAtom,
	vehicleDetailsAtom,
	vehicleDataLoaderAtom,
	ctpeuResponseStatusCodeAtom
} from '../../store/atoms/vehicleData';
import VehicleInfo from '../vehicleInfo/VehicleInfo';
import FeaturesSection from '../featuresSection/FeaturesSection';
import {
	InputErrorMessage,
	Loader,
	Text,
	MyTappOpenerCTA
} from '@tme/toyota-common';
import { hasConnectedFeatures, isPayloadEmpty } from './vinChecker.helper';
import {
	removeBracketFromUrlIfFallbackLabels,
	replacePlaceHoldersInLabel,
	replacePlaceHoldersInLabelObject
} from '../../tools/tools';
import { CONSTANTS } from '../../constants/constants';

const { HEADING_CLASS } = CONSTANTS;

export default function VinChecker({
	vinChecker,
	csFeatures,
	errorMessages
}: VinCheckerMainLabels): React.JSX.Element {
	const vehicleDetails = useRecoilValue(vehicleDetailsAtom);
	const ctpeuResponseStatusCode = useRecoilValue(ctpeuResponseStatusCodeAtom);
	const csFeaturesDataPayload = useRecoilValue(featuresAndCapabilitiesAtom);
	const vehicleDataLoader = useRecoilValue(vehicleDataLoaderAtom);
	const [showDownloadAppCTA, setShowDownloadAppCTA] =
		useState<boolean>(false);

	return (
		<div className={`${styles.wrapper}`}>
			<div className={`${styles.titleAndDescriptionWrapper}`}>
				<h1 className={HEADING_CLASS}>{vinChecker.title}</h1>
				<p>{replacePlaceHoldersInLabel(vinChecker.description)}</p>
			</div>
			<div
				className={`${styles.formWrapper} ${
					window.dxp.settings.brand === `lexus` &&
					styles.formWrapperLexusBackground
				}`}
			>
				<VinCheckerForm
					labels={vinChecker.form}
					errorLabels={errorMessages}
					setShowDownloadAppCTA={setShowDownloadAppCTA}
				/>
			</div>
			{vehicleDataLoader ? (
				<div className={styles.loader}>
					<Loader data-test-id={`vinchecker-form-loader`} />
				</div>
			) : (
				<>
					{!isPayloadEmpty(vehicleDetails) ? (
						<>
							{
								<div
									className={`${styles.vehicleInfoWrapper} ${
										window.dxp.settings.brand === `lexus` &&
										styles.vehicleInfoWrapperLexusBackground
									}`}
								>
									<VehicleInfo {...vehicleDetails} />
								</div>
							}
							<>
								{hasConnectedFeatures(csFeaturesDataPayload) ? (
									<FeaturesSection
										features={csFeaturesDataPayload}
										labels={csFeatures}
									/>
								) : (
									<div className={styles.errorMessage}>
										<Text
											kind={`CAPTION`}
											className={styles.infoMessage}
											testId={`no-cs-features-info-message`}
										>
											{errorMessages.noCSDataForVin}
										</Text>
									</div>
								)}
							</>
						</>
					) : (
						<>
							{ctpeuResponseStatusCode && (
								<div className={styles.errorMessage}>
									<InputErrorMessage
										{...{
											errorMessage:
												errorMessages.fetchingError,
											testId: `vehicle-details-error-message`,
											showErrorIcon: false,
											classNames: styles.errorMessage
										}}
									/>
								</div>
							)}
						</>
					)}
					{showDownloadAppCTA && (
						<div className={styles.myTappCta}>
							<MyTappOpenerCTA
								labels={removeBracketFromUrlIfFallbackLabels(
									replacePlaceHoldersInLabelObject(
										vinChecker.appLinksV2
									) as AppLinksV2Labels
								)}
								qrCodeSize={200}
							/>
						</div>
					)}
				</>
			)}
		</div>
	);
}
